import React from "react";

import { faPlus, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { Controller, UseFormReturn, useFieldArray } from "react-hook-form";

import { ManifestIntegrationProvider } from "src/api/connectApi/manifestTypes";
import { useConnection, useManifests } from "src/api/connectApi/queries";
import CustomConnectionIcon from "src/assets/CustomConnectionIcon.svg?react";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Option, Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Textarea } from "src/base-components/Textarea";
import { DocumentationType } from "src/clients/flow-api";
import { PromoteFlowVersionFormData } from "src/flowContainer/ManifestPromotion/types";
import { CustomConnectionNodeData } from "src/flowContainer/ManifestPromotion/utils";

type PromoteFlowVersionFormProps = {
  form: UseFormReturn<PromoteFlowVersionFormData>;
  baseUrl: string | undefined;
  customNodeData: CustomConnectionNodeData | null;
};

const DOCUMENTATION_TYPE_OPTIONS: { key: DocumentationType; value: string }[] =
  [
    { key: DocumentationType.PRODUCT_SPEC, value: "Product Spec" },
    { key: DocumentationType.API_REFERENCE, value: "API Reference" },
    { key: DocumentationType.INTEGRATION_GUIDE, value: "Integration Guide" },
    { key: DocumentationType.OTHER, value: "Other" },
  ];

export const PromoteFlowVersionForm: React.FC<PromoteFlowVersionFormProps> = ({
  form,
  baseUrl,
  customNodeData,
}) => {
  const { data: manifests, isLoading: manifestsLoading } =
    useManifests(baseUrl);
  const providerType = form.watch("providerType");

  const { data: connection, isLoading: connectionLoading } = useConnection({
    baseUrl,
    id: customNodeData?.connectionId,
    neverRefetch: true,
  });

  const providerOptions = getProviderOptions(manifests);

  const {
    fields: onePasswordFields,
    append: appendOnePassword,
    remove: removeOnePassword,
  } = useFieldArray({
    control: form.control,
    name: "onePasswordLinks",
  });

  const {
    fields: documentationFields,
    append: appendDocumentation,
    remove: removeDocumentation,
  } = useFieldArray({
    control: form.control,
    name: "documentations",
  });

  return (
    <div className="flex flex-col gap-6">
      <FormItem gap={false} label="Provider type" isRequired>
        <Controller
          control={form.control}
          name="providerType"
          render={({ field }) => (
            <SimpleRadioGroup
              orientation="horizontal"
              value={field.value}
              onValueChange={field.onChange}
            >
              <SimpleRadioGroup.Item
                dataLoc="provider-type-new"
                label={<span className="ml-2">New provider</span>}
                value="new"
              />
              <SimpleRadioGroup.Item
                dataLoc="provider-type-existing"
                label={<span className="ml-2">Existing provider</span>}
                value="existing"
              />
            </SimpleRadioGroup>
          )}
        />
      </FormItem>

      {providerType === "existing" && (
        <FormItem gap={false} label="Existing provider" isRequired>
          <Controller
            control={form.control}
            name="existingProviderName"
            render={({ field, fieldState }) => (
              <>
                <Select
                  dataLoc="existing-provider-select"
                  disabled={manifestsLoading}
                  errored={!!fieldState.error}
                  loading={manifestsLoading}
                  options={providerOptions}
                  placeholder={
                    manifestsLoading
                      ? "Loading providers..."
                      : "Select provider"
                  }
                  value={field.value ?? null}
                  searchable
                  onChange={field.onChange}
                />
                {fieldState.error && (
                  <ErrorHint>{fieldState.error.message}</ErrorHint>
                )}
              </>
            )}
          />
        </FormItem>
      )}

      {providerType === "new" && (
        <>
          <FormItem gap={false} label="Provider name" isRequired>
            <Input
              data-loc="provider-name-input"
              placeholder={
                connectionLoading ? "Loading..." : "Enter a provider name"
              }
              value={connection?.name ?? ""}
              disabled
              fullWidth
            />
          </FormItem>

          <FormItem gap={false} label="Resource name" isRequired>
            <Input
              data-loc="resource-name-input"
              placeholder="Enter a resource name"
              value={customNodeData?.nodeLabel ?? ""}
              disabled
              fullWidth
            />
          </FormItem>

          <FormItem gap={false} label="Provider description">
            <Controller
              control={form.control}
              name="newProviderDescription"
              render={({ field, fieldState }) => (
                <>
                  <Textarea
                    {...field}
                    data-loc="provider-description-textarea"
                    errored={!!fieldState.error}
                    placeholder="Describe what this provider does and its key capabilities (min 10 characters)"
                    fullWidth
                  />
                  {fieldState.error && (
                    <ErrorHint>{fieldState.error.message}</ErrorHint>
                  )}
                </>
              )}
            />
          </FormItem>
        </>
      )}
      <hr className="col-span-full border-t-gray-200" />

      <FormItem
        className="flex flex-col gap-2"
        gap={false}
        label="1Password links"
      >
        {onePasswordFields.map((field, index) => (
          <div
            key={field.id}
            className="flex items-center gap-2 rounded-lg bg-gray-50 p-3"
          >
            <div className="flex-1">
              <Controller
                control={form.control}
                name={`onePasswordLinks.${index}.url`}
                render={({ field: inputField, fieldState }) => (
                  <>
                    <Input
                      {...inputField}
                      data-loc={`one-password-link-input-${index}`}
                      errored={!!fieldState.error}
                      placeholder="https://.."
                      fullWidth
                    />
                    {fieldState.error && (
                      <ErrorHint>{fieldState.error.message}</ErrorHint>
                    )}
                  </>
                )}
              />
            </div>
            <Icon
              color="text-gray-500 hover:text-gray-700"
              dataLoc={`remove-one-password-link-${index}`}
              icon={faTrashAlt}
              size="xs"
              onClick={() => removeOnePassword(index)}
            />
          </div>
        ))}

        <div className="w-fit">
          <Button
            dataLoc="add-one-password-link"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => appendOnePassword({ url: "" })}
          >
            Add link
          </Button>
        </div>
      </FormItem>

      <FormItem
        className="flex flex-col gap-2"
        gap={false}
        label="Documentation"
      >
        {documentationFields.map((field, index) => (
          <div key={field.id} className="rounded-lg bg-gray-50 p-3">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Controller
                    control={form.control}
                    name={`documentations.${index}.type`}
                    render={({ field: selectField, fieldState }) => (
                      <>
                        <Select
                          dataLoc={`documentation-type-select-${index}`}
                          errored={!!fieldState.error}
                          options={DOCUMENTATION_TYPE_OPTIONS}
                          placeholder="Select documentation type"
                          value={selectField.value ?? null}
                          onChange={selectField.onChange}
                        />
                        {fieldState.error && (
                          <ErrorHint>{fieldState.error.message}</ErrorHint>
                        )}
                      </>
                    )}
                  />
                </div>
                <Icon
                  color="text-gray-500 hover:text-gray-700"
                  dataLoc={`remove-documentation-${index}`}
                  icon={faTrashAlt}
                  size="xs"
                  onClick={() => removeDocumentation(index)}
                />
              </div>
              <Controller
                control={form.control}
                name={`documentations.${index}.url`}
                render={({ field: inputField, fieldState }) => (
                  <>
                    <Input
                      {...inputField}
                      data-loc={`documentation-url-input-${index}`}
                      errored={!!fieldState.error}
                      placeholder="https://.."
                      fullWidth
                    />
                    {fieldState.error && (
                      <ErrorHint>{fieldState.error.message}</ErrorHint>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        ))}
        <div className="w-fit">
          <Button
            dataLoc="add-documentation"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() =>
              appendDocumentation({ url: "", type: DocumentationType.OTHER })
            }
          >
            Add documentation
          </Button>
        </div>
      </FormItem>
      <hr className="col-span-full border-t-gray-200" />

      <FormItem
        description="Describe any special error handling requirement"
        gap={false}
        label="Error handling notes"
      >
        <Controller
          control={form.control}
          name="errorHandling"
          render={({ field }) => (
            <Textarea
              {...field}
              data-loc="error-handling-textarea"
              placeholder="e.g., provider returns 200 with error codes in body"
              fullWidth
            />
          )}
        />
      </FormItem>

      <FormItem gap={false} label="Additional comments">
        <Controller
          control={form.control}
          name="comments"
          render={({ field }) => (
            <Textarea
              {...field}
              data-loc="comments-textarea"
              placeholder="Add any additional information for the integration team"
              fullWidth
            />
          )}
        />
      </FormItem>
    </div>
  );
};

const ProviderOptionLabel: React.FC<{
  displayName: string;
  iconPath: string | null;
}> = ({ displayName, iconPath }) => (
  <div className="flex items-center gap-2">
    {iconPath ? (
      <img
        alt={`${displayName} icon`}
        className="h-4 w-4 object-contain"
        src={iconPath}
      />
    ) : (
      <CustomConnectionIcon className="h-4 w-4" />
    )}
    <span>{displayName}</span>
  </div>
);

const getProviderOptions = (
  manifests: ManifestIntegrationProvider[] | undefined,
): Option[] => {
  if (!manifests) return [];
  return manifests.map((manifest) => ({
    key: manifest.name,
    value: (
      <ProviderOptionLabel
        displayName={manifest.display_name}
        iconPath={manifest.icon_path}
      />
    ),
    searchText: manifest.display_name,
  }));
};
